/**
 * Persistencia COMPARTIDA de la Matriz de Autoridades.
 *
 * Hasta ahora la matriz vivía únicamente en `localStorage`. Eso significaba
 * que todo lo que el ADMIN_PMIS configurara aplicaba SOLO en su navegador:
 * nadie más lo recibía, se perdía al limpiar los datos del sitio o al entrar
 * desde otro equipo, y dos administradores podían tener matrices distintas sin
 * saberlo. Para la superficie que gobierna quién puede hacer qué en toda la
 * aplicación, eso no es aceptable.
 *
 * Ahora la fuente de verdad es el backend, en una fila
 * `entity="config", id="pmis_matriz_permisos"` — el mismo patrón que ya usa el
 * mapa de credenciales (`credencialesStore`).
 *
 * `localStorage` NO desaparece: pasa a ser un ESPEJO local. Es lo que permite
 * que `leerMatriz()` siga siendo síncrona, y con ella `usePermissions.can()`,
 * que se evalúa en cada render de cada pantalla y no puede volverse asíncrona
 * sin reescribir media aplicación. El flujo queda:
 *
 *   arranque  → se hidrata el espejo desde el backend y se avisa a la interfaz
 *   lectura   → síncrona contra el espejo
 *   escritura → espejo primero (efecto inmediato) y backend después
 */

import type { MatrizPermisos } from "./matrizPermisos";

const CONFIG_ID = "pmis_matriz_permisos";
const BACKEND = import.meta.env.VITE_BACKEND ?? "sharepoint";
const TENANT = "local-dev";

export interface MatrizPersistida {
  activa?: MatrizPermisos;
  porDefecto?: MatrizPermisos;
}

/** ¿La matriz se comparte entre equipos, o es solo de este navegador? */
export const MATRIZ_ES_COMPARTIDA = BACKEND === "supabase";

async function cliente() {
  return import("@core/data/adapters/supabase/supabaseClient");
}

/** Lee la matriz compartida. `null` si no hay backend compartido o no existe. */
export async function cargarMatrizRemota(): Promise<MatrizPersistida | null> {
  if (!MATRIZ_ES_COMPARTIDA) return null;
  try {
    const { supabase, TABLE } = await cliente();
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", "config")
      .eq("tenant_id", TENANT)
      .eq("id", CONFIG_ID)
      .maybeSingle();
    if (error || !data) return null;
    return ((data as { payload?: MatrizPersistida }).payload ?? null) as MatrizPersistida | null;
  } catch {
    // Sin red o sin permisos: se sigue con el espejo local. Degradar a la
    // configuración del navegador es preferible a dejar la app sin permisos.
    return null;
  }
}

/**
 * Publica la matriz para toda la organización.
 *
 * Lanza si falla, a diferencia de la lectura: al guardar, el administrador
 * tiene que enterarse de que su cambio NO quedó compartido — si no, creería
 * que delegó un permiso y solo lo tendría él.
 */
export async function guardarMatrizRemota(datos: MatrizPersistida): Promise<void> {
  if (!MATRIZ_ES_COMPARTIDA) return;
  const { supabase, TABLE } = await cliente();
  const now = new Date().toISOString();
  const { error } = await supabase()
    .from(TABLE)
    .upsert(
      {
        entity: "config",
        id: CONFIG_ID,
        tenant_id: TENANT,
        version: 1,
        payload: datos,
        created_at: now,
        updated_at: now,
      },
      { onConflict: "entity,id" },
    );
  if (error) {
    throw new Error(`No se pudo publicar la matriz de permisos: ${error.message}`);
  }
}
